import { CancelRounded, CheckCircleRounded, DoNotDisturbRounded, FiberNewRounded, HelpOutlineRounded, MopedRounded, NavigateNextRounded, RunCircleRounded } from "@mui/icons-material";
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Chip, IconButton, Box, Tooltip, Stack, ClickAwayListener, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Popper } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Employee } from "../../types/Employee";
import { PassportRequest, PassportRequestStatus } from "../../types/PassportRequest";
import { ReceiptRequest, ReceiptRequestType } from "../../types/ReceiptRequest";
import { RequestStatus } from "../../types/Type";
import { queryPassportRequestsByIdNumber } from "../../utils/queryPassportRequests";
import { queryReceiptRequestsByIdNumber } from "../../utils/queryReceiptRequests";
import { updatePassportRequest } from "../../utils/updatePassportRequest";
import { updateReceiptRequest } from "../../utils/updateReceiptRequest";
import { ROLES_PERMISSIONS_MAPPING } from "../../security/Role";
import { SecureComponent } from "../../security/SecureComponent";
import { hasPermission } from "../../security/Permission";
import { useAppSelector } from "../../store";

export const ChipIcon = (status: PassportRequestStatus) => {
  if (status === 'New')
    return <FiberNewRounded />
  else if (status === 'Processing')
    return <RunCircleRounded />
  else if (status === 'Available')
    return <MopedRounded />
  else if (status === 'Completed')
    return <CheckCircleRounded />
  else
    return <CancelRounded />
};

export const ChipLabel = (status: PassportRequestStatus) => {
  if (status === 'New')
    return 'New';
  else if (status === 'Processing')
    return 'Working...';
  else if (status === 'Available')
    return 'Ready for Pickup';
  else if (status === 'Completed')
    return 'Completed';
  else
    return 'Cancelled';
};

export const ChipColor = (status: PassportRequestStatus) => {
  if (status === 'New')
    return 'info';
  else if (status === 'Processing')
    return 'warning';
  else if (status === 'Available')
    return 'secondary';
  else if (status === 'Completed')
    return 'success';
  else
    return 'error';
};

export const EmployeePassportRequests = (props: { employee: Employee }) => {
  const { employee } = props;
  const navigate = useNavigate();
  const authState = useAppSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const [passportRequests, setPassportRequests] = useState<PassportRequest[]>([]);
  const [selected, setSelected] = useState<PassportRequest>();
  const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(null);

  const handleUpdatePassportRequest = (s: PassportRequestStatus) => {
    if (selected) {
      updatePassportRequest(selected, s).then(() => {
        let message = `Employee '${selected.idNumber}' passport request has been update.`;
        if (s === 'Processing')
          message = `Employee '${selected.idNumber}' passport request is now being processed.`;
        else if (s === 'Available')
          message = `Employee '${selected.idNumber}' passport request is now ready for pickup.`;
        else if (s === 'Completed')
          message = `Employee '${selected.idNumber}' passport request is now tagged as complete.`;
        else if (s === 'Cancelled')
          message = `Employee '${selected.idNumber}' passport request is now cancelled.`;

        const updatedValue = { ...selected, status: s };

        // Update selected and list
        const copyA = [...passportRequests];
        const idxA = copyA.findIndex((e) => e.id === selected.id);
        if (idxA !== -1) copyA[idxA] = updatedValue;
        setPassportRequests(copyA);
        setSelected(updatedValue);
        enqueueSnackbar(message, { variant: 'success' });
      });
    }
  };

  useEffect(() => {
    queryPassportRequestsByIdNumber(employee.idNumber).then((passportRequests) => {
      setPassportRequests(passportRequests);
    });
  }, []);

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={76} align="right">Count</TableCell>
              <TableCell width={120}>Id Number</TableCell>
              <TableCell>Name</TableCell>
              <TableCell width={120}>Type</TableCell>
              <TableCell width={220}>Date Requested</TableCell>
              <TableCell width={200}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {passportRequests.length === 0 && (
              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 } 
                }}>
                <TableCell colSpan={6}><Typography variant="caption">No passport requests to show...</Typography></TableCell>
              </TableRow>
            )}
            {passportRequests.map((p, i) => {
              return (
                <TableRow 
                  hover={hasPermission(authState, [], ['update.passportRequests'])}
                  selected={selected?.id === p.id}
                  key={p.id}
                  sx={{
                    cursor: hasPermission(authState, [], ['update.passportRequests']) ? 'pointer' : 'default',
                    '&:last-child td, &:last-child th': { border: 0 } 
                  }}
                  onClick={() => {
                    if (hasPermission(authState, [], ['update.passportRequests'])) {
                      const s = selected?.id === p.id ? undefined : p;
                      setSelected(s);
                      if (s)
                        setPopperAnchorEl(document.getElementById(s.id));
                      else
                        setPopperAnchorEl(null);
                    }
                  }}
                >
                  <TableCell align="right">{i + 1}</TableCell>
                  <TableCell>{p.idNumber}</TableCell>
                  <TableCell>
                    <span id={p.id}>{p.name}</span>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography variant="caption">
                        {p.type}
                      </Typography>
                      {p.type === 'Copy' && (
                        <Tooltip title={p.reason} arrow>
                          <HelpOutlineRounded color="info" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{p.dateRequested ? DateTime.fromISO(p.dateRequested).toLocaleString(DateTime.DATETIME_MED) : ''}</TableCell>
                  <TableCell>
                    <Chip icon={ChipIcon(p.status)} label={ChipLabel(p.status)} color={ChipColor(p.status)} variant="outlined" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.passport']}>
        <Popper open={selected !== undefined} anchorEl={popperAnchorEl} placement='right' sx={{ zIndex: 999 }}>
          <ClickAwayListener onClickAway={() => setSelected(undefined)}>
            <Paper 
              sx={{ 
                ml: 3,
              }} 
              elevation={12}
            >
              <List sx={{ minWidth: 250 }}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleUpdatePassportRequest('Processing')} disabled={selected?.status !== 'New'}>
                    <ListItemIcon>
                      <RunCircleRounded color="warning" />
                    </ListItemIcon>
                    <ListItemText primary="Set as Processing" />
                  </ListItemButton>
                </ListItem>
                <Divider />
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleUpdatePassportRequest('Available')} disabled={selected?.status !== 'New' && selected?.status !== 'Processing'}>
                    <ListItemIcon>
                      <MopedRounded color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary="Set as Ready for Pickup" />
                  </ListItemButton>
                </ListItem>
                <Divider />
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleUpdatePassportRequest('Completed')} disabled={selected?.status === 'Completed' || selected?.status === 'Cancelled'}>
                    <ListItemIcon>
                      <CheckCircleRounded color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Set as Completed" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </SecureComponent>
    </Paper>
  )
};